import { memo } from 'react';

import type { SideNavItem } from '../types';

import { useSideNavContext } from '../context';
import { isItemActive } from '../utils';
import { SideNavControl } from './SideNavControl';
import { SideNavFlyout } from './SideNavFlyout';
import { SideNavItemIcon } from './SideNavItemRow';

function ignoreActivation() {}

interface SideNavRailItemProps {
  item: SideNavItem;
  /** Keeps the entry from ever reading as current, for a row that only labels a flyout. */
  neverCurrent?: boolean;
}

/**
 * One entry of the collapsed rail: icon over label, with a flyout for a group.
 * An entry with nested links and no `href` only labels its flyout, so pressing
 * it reports nothing.
 */
export const SideNavRailItem = memo(function SideNavRailItem({
  item,
  neverCurrent = false,
}: SideNavRailItemProps) {
  const { activeKey, onActivate } = useSideNavContext();
  const isActive = !neverCurrent && isItemActive(item, activeKey);
  const isFlyoutLabel =
    item.href === undefined && Boolean(item.children?.length);

  const control = (
    <SideNavControl
      className="eds-side-nav--rail-item"
      isCurrent={isActive}
      link={item}
      onActivate={isFlyoutLabel ? ignoreActivation : onActivate}
    >
      <span aria-hidden="true" className="eds-side-nav--rail-icon">
        <SideNavItemIcon isActive={isActive} item={item} />
        {item.railBadge && (
          <span className="eds-side-nav--rail-badge">{item.railBadge}</span>
        )}
      </span>
      <span className="eds-side-nav--rail-label">{item.label}</span>
    </SideNavControl>
  );

  if (!item.children?.length) {
    return control;
  }

  return (
    <SideNavFlyout label={item.label} links={item.children}>
      {control}
    </SideNavFlyout>
  );
});
